import { useRef, type PointerEvent, type ReactNode } from 'react'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/Drawer'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { useDrawerWidth } from '@/hooks/useDrawerWidth'
import { useHelpFeedbackStore, type PanelTab } from '@/stores/helpFeedbackStore'
import { usePanelControls } from './helpFeedbackContext'
import { GRABBER, TAB_ASK, TAB_MINE, TITLE } from './helpFeedbackText'

/** How far down the phone grabber must travel to close the panel. */
export const GRAB_CLOSE_PX = 80

interface Props {
  open: boolean
  ask: ReactNode
  mine: ReactNode
  /** Where focus goes back to once the panel has closed. */
  onClosed: () => void
  /** What takes the focus as the panel opens, when not its first control (SA-1316: an answer's Yes). */
  focusOnOpen?: () => HTMLElement | null
}

/**
 * The Help & feedback drawer (SA-1310 spec §3): beside the page, never over it. No backdrop, a click
 * on the page leaves it open, Esc stops the mic first and otherwise closes it, and closing hands focus
 * back to whatever had it. Below `sm` it is the drawer's full-width sheet with a grabber to drag down.
 */
export function HelpFeedbackPanel({ open, ask, mine, onClosed, focusOnOpen }: Props) {
  const { close, escape } = usePanelControls()
  const tab = useHelpFeedbackStore((s) => s.tab)
  const setTab = useHelpFeedbackStore((s) => s.setTab)
  const resize = useDrawerWidth('help-feedback', { default: 420, min: 360 }, 'Resize help & feedback panel')
  const grabStart = useRef<number | null>(null)

  const onGrabDown = (e: PointerEvent<HTMLDivElement>) => {
    grabStart.current = e.clientY
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onGrabUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = grabStart.current
    grabStart.current = null
    if (start !== null && e.clientY - start >= GRAB_CLOSE_PX) close()
  }

  return (
    <Drawer open={open} modal={false} onOpenChange={(next) => !next && close()}>
      <DrawerContent
        overlay={false}
        resize={resize}
        aria-label={TITLE}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (escape.current?.()) e.preventDefault()
        }}
        onOpenAutoFocus={(e) => {
          const to = focusOnOpen?.()
          if (!to) return
          e.preventDefault()
          to.focus()
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          onClosed()
        }}
      >
        <div
          aria-hidden
          title={GRABBER}
          data-testid="help-feedback-grabber"
          className="flex touch-none justify-center pt-2 sm:hidden"
          onPointerDown={onGrabDown}
          onPointerUp={onGrabUp}
          onPointerCancel={() => (grabStart.current = null)}
        >
          <span className="h-1 w-10 rounded-full bg-muted-foreground/40" />
        </div>
        <DrawerHeader className="py-3">
          <DrawerTitle>{TITLE}</DrawerTitle>
        </DrawerHeader>
        <Tabs value={tab} onValueChange={(v) => setTab(v as PanelTab)} className="flex min-h-0 flex-1 flex-col gap-0">
          <TabsList className="mx-5 mt-3 grid w-auto grid-cols-2">
            <TabsTrigger value="ask">{TAB_ASK}</TabsTrigger>
            <TabsTrigger value="mine">{TAB_MINE}</TabsTrigger>
          </TabsList>
          {/* Ask stays mounted on Your feedback, so words being typed are kept. */}
          <TabsContent value="ask" forceMount className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
            {ask}
          </TabsContent>
          <TabsContent value="mine" className="min-h-0 flex-1 overflow-y-auto">
            {mine}
          </TabsContent>
        </Tabs>
      </DrawerContent>
    </Drawer>
  )
}
